import type { ZodType } from "zod";
import z from "zod";

import {
  AssessmentIdSchema,
  FinishedAssessmentStatusSchema,
  PinnedInstrumentSchema,
  type AssessmentIdUnbranded,
  type FinishedAssessmentStatus,
  type PinnedInstrumentUnbranded,
} from "./assessment.js";
import { UserIdSchema, type UserIdUnbranded } from "./user.js";

/**
 * Whether a finished assessment may give a new one its ratings, a check per
 * condition, so that what held an offer back can be named.
 */
export type CarryOverChecks = {
  /** It finished no more than the body's configured number of days before the new one starts. */
  withinTimeLimit: boolean;
  /** The young person has not been unenrolled since it finished; passes until unenrolment exists. */
  notUnenrolledSince: boolean;
  /** Its assessor belongs to the starter's organisation or the one above it; passes until that rule is wanted. */
  sameOrganisationTree: boolean;
};

/**
 * The finished assessment a new one would carry ratings over from, as it is
 * offered.
 *
 * Both names travel with it: the starter reads the young person's record, but
 * may not read another assessor's or an approver's account to look the names
 * up.
 */
export type CarryOverSourceUnbranded = {
  assessmentId: AssessmentIdUnbranded;
  instrument: PinnedInstrumentUnbranded;
  status: FinishedAssessmentStatus;
  /** When it was approved, or when the submission that completed it was made. */
  finishedAt: Date;
  assessorUserId: UserIdUnbranded;
  assessorName: string;
  /** Who approved it; null for one that needed no approval. */
  approverName: string | null;
};

export const CarryOverSourceSchema = z.object({
  assessmentId: AssessmentIdSchema,
  instrument: PinnedInstrumentSchema,
  status: FinishedAssessmentStatusSchema,
  finishedAt: z.coerce.date(),
  assessorUserId: UserIdSchema,
  assessorName: z.string(),
  approverName: z.string().nullable(),
}) satisfies ZodType<CarryOverSourceUnbranded>;

export type CarryOverSource = z.infer<typeof CarryOverSourceSchema>;

/**
 * What starting an assessment now would be offered to carry over: the finished
 * assessment it would follow, where there is one and it passes every check.
 */
export type CarryOverOfferUnbranded = {
  source: CarryOverSourceUnbranded | null;
};

export const CarryOverOfferSchema = z.object({
  source: CarryOverSourceSchema.nullable(),
}) satisfies ZodType<CarryOverOfferUnbranded>;

export type CarryOverOffer = z.infer<typeof CarryOverOfferSchema>;
